import { forwardRef, useEffect, useId, useState, type ReactNode } from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';

export interface LecartteSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface LecartteSelectProps {
  /** Single is the only mode in this version. Multiple comes later. */
  mode?: 'single';
  label?: string;
  helperText?: string;
  error?: string | null;
  options: ReadonlyArray<LecartteSelectOption>;
  /** `null` shows the placeholder. Radix rejects an item with `value=""`. */
  value?: string | null;
  defaultValue?: string | null;
  placeholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  size?: 'sm' | 'md';
  tone?: 'default' | 'menu';
  testId?: string;
  className?: string;
  contentClassName?: string;
  name?: string;
  id?: string;
  required?: boolean;
  ariaLabel?: string;
  onValueChange?: (args: { value: string }) => void;
  onBlur?: () => void;
}

const joinClasses = ({ classes }: { classes: Array<string | false | null | undefined> }) =>
  classes.filter(Boolean).join(' ');

const useShakeOnError = ({ error }: { error?: string | null }) => {
  const [shouldShake, setShouldShake] = useState(false);

  useEffect(() => {
    if (!error) return;
    setShouldShake(true);
    const timer = setTimeout(() => setShouldShake(false), 500);
    return () => clearTimeout(timer);
  }, [error]);

  return shouldShake;
};

const selectableOptions = ({ options }: { options: ReadonlyArray<LecartteSelectOption> }) =>
  options.filter((option) => option.value !== '');

const FieldMessage = ({ id, children, error }: { id: string; children: ReactNode; error?: boolean }) => (
  <p id={id} role={error ? 'alert' : undefined} className={error ? 'text-xs text-red-600' : 'text-xs text-slate-500'}>
    {children}
  </p>
);

/**
 * Canonical select. Screens should use this instead of `Select` / `FormSelect`.
 * `value: null` (or `""`) shows the placeholder — do not put an item with an empty value.
 */
export const LecartteSelect = forwardRef<HTMLButtonElement, LecartteSelectProps>(function LecartteSelect(
  {
    label,
    helperText,
    error,
    options,
    value,
    defaultValue,
    placeholder,
    emptyMessage = 'Nenhuma opção',
    disabled = false,
    size = 'md',
    tone = 'default',
    testId,
    className,
    contentClassName,
    name,
    id,
    required = false,
    ariaLabel,
    onValueChange,
    onBlur,
  },
  ref
) {
  const generatedId = useId();
  const selectId = id ?? generatedId;
  const helperId = helperText ? `${selectId}-helper` : undefined;
  const errorId = error ? `${selectId}-error` : undefined;
  const describedBy = [helperId, errorId].filter(Boolean).join(' ') || undefined;
  const shouldShake = useShakeOnError({ error });
  const items = selectableOptions({ options });
  const isMenu = tone === 'menu';
  const isControlled = value !== undefined;
  const radixValue = !isControlled ? undefined : value == null || value === '' ? '' : value;

  const triggerClasses = joinClasses({
    classes: [
      'flex w-full items-center justify-between gap-2 rounded-2xl border bg-white/80 text-left outline-none transition focus:ring-2 disabled:cursor-not-allowed disabled:bg-slate-100/70 disabled:text-slate-500',
      size === 'sm' ? 'px-3 py-1.5 text-xs' : 'px-4 py-3 text-sm',
      isMenu
        ? 'border-[var(--theme-card-border,#e2e8f0)] bg-[var(--theme-card-bg,rgba(255,255,255,0.8))] text-[var(--theme-heading,#334155)] focus:border-[var(--theme-primary,var(--color-primary))] focus:ring-[var(--theme-primary,var(--color-primary))]/20'
        : 'text-slate-700',
      !isMenu && (error
        ? 'border-red-300 focus:border-red-300 focus:ring-red-100'
        : 'border-slate-200 focus:border-success-200 focus:ring-success-100'),
      error && isMenu ? 'border-red-300 focus:border-red-300 focus:ring-red-100' : '',
      shouldShake ? 'animate-shake' : '',
    ],
  });

  const contentClasses = joinClasses({
    classes: [
      'z-[100] max-h-60 w-[var(--radix-select-trigger-width)] overflow-hidden rounded-2xl border p-1 shadow-lg',
      isMenu
        ? 'border-[var(--theme-card-border,#e2e8f0)] bg-[var(--theme-card-bg,#fff)] text-[var(--theme-heading,#1f160f)]'
        : 'border-slate-200 bg-white text-slate-700',
      contentClassName,
    ],
  });

  const itemClasses = joinClasses({
    classes: [
      'relative flex cursor-pointer select-none items-center rounded-xl py-2 pl-3 pr-8 text-sm outline-none',
      'data-[disabled]:pointer-events-none data-[disabled]:opacity-40',
      isMenu
        ? 'data-[highlighted]:bg-black/[0.06] data-[state=checked]:font-semibold data-[state=checked]:text-[var(--theme-primary,var(--color-primary))]'
        : 'data-[highlighted]:bg-slate-50 data-[state=checked]:font-semibold data-[state=checked]:text-[var(--color-primary)]',
      size === 'sm' ? 'text-xs' : '',
    ],
  });

  return (
    <div className={joinClasses({ classes: ['flex flex-col gap-1.5', className] })}>
      {label && (
        <label htmlFor={selectId} className="block text-sm font-semibold text-slate-700">
          <span className={isMenu
            ? 'text-xs uppercase tracking-[0.3em] text-[var(--theme-text-soft,#94a3b8)]'
            : 'text-xs uppercase tracking-[0.3em] text-slate-400'}
          >
            {label}
            {required ? ' *' : ''}
          </span>
        </label>
      )}

      <SelectPrimitive.Root
        value={radixValue}
        defaultValue={isControlled ? undefined : defaultValue ?? undefined}
        disabled={disabled}
        onValueChange={(next) => onValueChange?.({ value: next })}
      >
        <SelectPrimitive.Trigger
          ref={ref}
          id={selectId}
          data-testid={testId}
          aria-label={ariaLabel}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          aria-required={required || undefined}
          onBlur={onBlur}
          className={triggerClasses}
        >
          <span className="min-w-0 flex-1 truncate text-left">
            <SelectPrimitive.Value placeholder={placeholder} />
          </span>
          <SelectPrimitive.Icon asChild>
            <ChevronDown className="h-4 w-4 shrink-0 opacity-60" aria-hidden />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>

        <SelectPrimitive.Portal>
          <SelectPrimitive.Content position="popper" sideOffset={6} className={contentClasses}>
            <SelectPrimitive.Viewport className="max-h-56 overflow-y-auto">
              {items.length === 0 ? (
                <div className="px-3 py-2 text-sm text-slate-500">{emptyMessage}</div>
              ) : (
                items.map((option) => (
                  <SelectPrimitive.Item
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                    className={itemClasses}
                  >
                    <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                    <span className="absolute right-2 flex h-4 w-4 items-center justify-center">
                      <SelectPrimitive.ItemIndicator>
                        <Check className="h-4 w-4" aria-hidden />
                      </SelectPrimitive.ItemIndicator>
                    </span>
                  </SelectPrimitive.Item>
                ))
              )}
            </SelectPrimitive.Viewport>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>

      {name ? <input type="hidden" name={name} value={value ?? defaultValue ?? ''} readOnly /> : null}
      {helperText && helperId ? <FieldMessage id={helperId}>{helperText}</FieldMessage> : null}
      {error && errorId ? <FieldMessage id={errorId} error>{error}</FieldMessage> : null}
    </div>
  );
});

LecartteSelect.displayName = 'LecartteSelect';
